import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Card, Typography, Row, Col, Space, Flex } from "antd";
import {
  DesktopOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  PrinterOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { getFetch, API_URL, getWorkstationTranslationKey } from "../shared/utils/Util";
import { isAdmin, isManager, hasManagerAccess } from "../shared/utils/Auth";
import { useAntdTheme } from "../shared/hooks";

const { Title, Text } = Typography;

interface WorkstationSchema {
  id: string;
  name: string;
}

interface ControlStation {
  name: string;
  workstation_schemas: WorkstationSchema[] | null;
}

interface ControlStationsResponse {
  control_stations: ControlStation[];
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useAntdTheme();

  const [controlStations, setControlStations] = useState<ControlStation[]>([]);

  useEffect(() => {
    const restaurantId = sessionStorage.getItem("restaurant_id");
    if (!restaurantId) {
      return;
    }
    getFetch(`${API_URL}/v1/r/${restaurantId}/control_stations`, (res: ControlStationsResponse) => {
      if (res && res.control_stations && res.control_stations.length > 0) {
        setControlStations(res.control_stations);
      }
    });
  }, []);

  // Create station card helper - now using AntD components properly
  const createStationCard = (
    icon: React.ReactNode,
    title: string,
    onClick: () => void,
    color: string = token.colorPrimary
  ) => (
    <Card
      hoverable
      onClick={onClick}
      styles={{
        body: {
          textAlign: "center",
          minHeight: 120,
        },
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div style={{ fontSize: token.fontSizeHeading1, color }}>{icon}</div>
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
      </Space>
    </Card>
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Workstations Section */}
      {controlStations.length > 0 && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Title level={3}>{t("workstations")}</Title>
          <Row gutter={[16, 16]}>
            {controlStations.map((cs) =>
              cs.workstation_schemas?.map((ws) => {
                const translationKey = getWorkstationTranslationKey(ws.name);
                const translatedName = translationKey.startsWith("workstation_")
                  ? t(translationKey)
                  : ws.name;

                const hasStationWord =
                  translatedName.toLowerCase().includes("station") || translatedName.includes("台");
                const displayName = hasStationWord
                  ? translatedName
                  : `${translatedName} ${t("station")}`;

                return (
                  <Col key={ws.id} xs={24} sm={12} md={8} lg={6}>
                    {createStationCard(
                      <DesktopOutlined />,
                      displayName,
                      () => selectWorkstation(navigate, ws),
                      token.colorPrimary
                    )}
                  </Col>
                );
              })
            )}
          </Row>
        </Space>
      )}

      {/* Tools Section */}
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Title level={3}>{t("tools")}</Title>
        <Row gutter={[16, 16]}>
          {/* Master Control Station */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              hoverable
              onClick={() => navigate("/master-control-station")}
              styles={{
                body: {
                  textAlign: "center",
                  minHeight: 120,
                },
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ fontSize: token.fontSizeHeading1, color: token.colorPrimary }}>
                  <DesktopOutlined />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  {t("master_control_station")}
                </Title>
              </Space>
            </Card>
          </Col>

          {/* Completed Orders */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              hoverable
              onClick={() => navigate("/done-order-view")}
              styles={{
                body: {
                  textAlign: "center",
                  minHeight: 120,
                },
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ fontSize: token.fontSizeHeading1, color: token.colorSuccess }}>
                  <CheckCircleOutlined />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  {t("completed_orders")}
                </Title>
              </Space>
            </Card>
          </Col>

          {/* Order Lookup */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              hoverable
              onClick={() => navigate("/order-lookup")}
              styles={{
                body: {
                  textAlign: "center",
                  minHeight: 120,
                },
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ fontSize: token.fontSizeHeading1, color: token.colorInfo }}>
                  <SearchOutlined />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  {t("order_lookup")}
                </Title>
              </Space>
            </Card>
          </Col>

          {/* Printer Management */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              hoverable
              onClick={() => navigate("/printer-management")}
              styles={{
                body: {
                  textAlign: "center",
                  minHeight: 120,
                },
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div style={{ fontSize: token.fontSizeHeading1, color: token.colorInfo }}>
                  <PrinterOutlined />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  {t("printer_management")}
                </Title>
              </Space>
            </Card>
          </Col>
        </Row>
      </Space>

      {/* Manager Dashboard Section */}
      {isManager() && !isAdmin() && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Title level={3}>{t("manager_functions")}</Title>
          <Card
            hoverable
            onClick={() => navigate("/manager")}
            style={{
              background: token.colorBgContainer,
            }}
          >
            <Flex justify="space-between" align="center">
              <Flex gap="large" align="center">
                <SettingOutlined
                  style={{
                    fontSize: token.fontSizeHeading1,
                    color: token.colorInfo,
                  }}
                />
                <Space direction="vertical" size={0}>
                  <Title level={3} style={{ margin: 0, color: token.colorInfo }}>
                    {t("manager_dashboard")}
                  </Title>
                  <Text type="secondary">{t("manager_dashboard_description")}</Text>
                </Space>
              </Flex>
              <ArrowRightOutlined
                style={{
                  fontSize: token.fontSizeHeading3,
                  color: token.colorInfo,
                }}
              />
            </Flex>
          </Card>
        </Space>
      )}

      {/* Admin Dashboard Section */}
      {isAdmin() && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Title level={3}>{t("admin_functions")}</Title>
          <Card
            hoverable
            onClick={() => navigate("/admin")}
            style={{
              background: token.colorBgContainer,
            }}
          >
            <Flex justify="space-between" align="center">
              <Flex gap="large" align="center">
                <SettingOutlined
                  style={{
                    fontSize: token.fontSizeHeading1,
                    color: token.colorPrimary,
                  }}
                />
                <Space direction="vertical" size={0}>
                  <Title level={3} style={{ margin: 0, color: token.colorPrimary }}>
                    {t("admin_dashboard")}
                  </Title>
                  <Text type="secondary">{t("admin_dashboard_description")}</Text>
                </Space>
              </Flex>
              <ArrowRightOutlined
                style={{
                  fontSize: token.fontSizeHeading3,
                  color: token.colorPrimary,
                }}
              />
            </Flex>
          </Card>
        </Space>
      )}
    </Space>
  );
};

const selectWorkstation = (navigate: ReturnType<typeof useNavigate>, ws: WorkstationSchema) => {
  navigate("/work-station", {
    state: {
      workStationID: ws.id,
      workStationName: ws.name,
    },
  });
};

export default Home;
